import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Alert, Card, Form, Button, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    getEmailTemplates, updateEmailTemplate, getEmailBranding,
    getEmailLayout, updateEmailLayout, resetEmailLayoutSection, sendEmailTemplateTest, applySharedEmailLayout,
} from '../../../services/SettingsService';
import EmailBrandingSection from './EmailBrandingSection';
import RichTextEditor from './EmailTemplateEditor/RichTextEditor';
import EmailPreview from './EmailTemplateEditor/EmailPreview';
import BrandedEmailPreview from './EmailTemplateEditor/BrandedEmailPreview';
import EmailLayoutSettings from './EmailTemplateEditor/EmailLayoutSettings';

const TEMPLATE_KEYS = ['order_confirmation', 'ready_to_deliver', 'out_for_delivery', 'delivered', 'review_request']; // order confirmation = the first email, review request = the last
const PLACEHOLDER_TOKENS = ['{customer_name}', '{order_number}', '{items}', '{delivery_date}', '{expected_delivery}', '{delivery_window}', '{delivered_at}', '{tracking_link}', '{order_link}'];

export default function EmailTemplatesSettings() {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState({}); // { [key]: { subject, body, include_logo, include_signature, attach_invoice_pdf, use_branded_layout } }
    // Branded layout settings per template: { schema, initial (form start values), baseline (as saved), version (remounts the form) }
    const [layouts, setLayouts] = useState({});
    const [drafts, setDrafts] = useState({}); // live, unsaved form values per template (feeds the preview)
    const [layoutValid, setLayoutValid] = useState({});
    const [layoutErrors, setLayoutErrors] = useState({}); // server-side problems per template: { [settingKey]: { code } }
    const [layoutFailed, setLayoutFailed] = useState({});
    const [subTabs, setSubTabs] = useState({}); // 'branded' (default) | 'old'
    const [testTo, setTestTo] = useState({}); // recipient of the test email per template (defaults to meta.test_recipient)
    const [busy, setBusy] = useState({}); // { [key]: 'test' | 'share' } while a request is running
    const [branding, setBranding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);
    const [activeKey, setActiveKey] = useState(TEMPLATE_KEYS[0]);

    const load = async () => {
        try {
            setLoading(true);
            const [templatesRes, brandingRes] = await Promise.all([
                getEmailTemplates(),
                getEmailBranding().catch(() => ({ data: null })),
            ]);
            const byKey = {};
            for (const row of templatesRes.data || []) {
                byKey[row.template_key] = {
                    subject: row.subject || '',
                    body: row.body || '',
                    include_logo: row.include_logo !== false,
                    include_signature: row.include_signature !== false,
                    attach_invoice_pdf: !!row.attach_invoice_pdf,
                    use_branded_layout: !!row.use_branded_layout,
                };
            }
            setTemplates(byKey);
            setBranding(brandingRes.data);
        } catch {
            toast.error(t('emailTemplates.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const setField = (key, field, value) => {
        setTemplates((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    };

    const loadLayout = async (key) => {
        try {
            const res = await getEmailLayout(key);
            setLayouts((prev) => ({ ...prev, [key]: { schema: res.data.schema, initial: res.data.values, baseline: res.data.values, meta: res.data.meta, version: 0 } }));
            setDrafts((prev) => ({ ...prev, [key]: res.data.values }));
            setLayoutFailed((prev) => ({ ...prev, [key]: false }));
        } catch {
            setLayoutFailed((prev) => ({ ...prev, [key]: true }));
            toast.error(t('emailTemplates.layout.loadError'));
        }
    };

    useEffect(() => {
        if (!loading && !layouts[activeKey] && !layoutFailed[activeKey]) loadLayout(activeKey);
    }, [activeKey, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLayoutReset = async (key, sectionId) => {
        try {
            const res = await resetEmailLayoutSection(key, sectionId);
            const sectionKeys = layouts[key].schema.find((g) => g.id === sectionId).rows.flatMap((r) => r.fields.map((f) => f.key));
            // only the reset section takes the default; unsaved edits in other sections are kept
            const merged = { ...drafts[key] };
            sectionKeys.forEach((k) => { merged[k] = res.data.values[k]; });
            setDrafts((prev) => ({ ...prev, [key]: merged }));
            setLayouts((prev) => ({ ...prev, [key]: { ...prev[key], initial: merged, baseline: res.data.values, version: prev[key].version + 1 } }));
            setLayoutErrors((prev) => ({ ...prev, [key]: {} }));
            toast.success(t('emailTemplates.layout.resetDone'));
        } catch {
            toast.error(t('emailTemplates.layout.resetError'));
        }
    };

    const sendTest = async (key, to, confirmExternal) => {
        return sendEmailTemplateTest(key, {
            to, subject: templates[key]?.subject, settings: drafts[key], confirm_external: confirmExternal || undefined,
        });
    };

    const handleSendTest = async (key) => {
        const layout = layouts[key];
        if (!layout) return;
        if (layoutValid[key] === false) {
            toast.error(t('emailTemplates.layout.invalidForm'));
            return;
        }
        const to = (testTo[key] ?? layout.meta.test_recipient).trim();
        const domain = to.includes('@') ? to.split('@').pop().toLowerCase() : '';
        let confirmExternal = false;
        if (domain && !layout.meta.own_domains.includes(domain)) {
            const result = await Swal.fire({
                title: t('emailTemplates.test.externalTitle'),
                text: t('emailTemplates.test.externalText', { to, domains: layout.meta.own_domains.join(', ') }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('emailTemplates.test.externalConfirm'),
                cancelButtonText: t('common.cancel'),
            });
            if (!result.isConfirmed) return;
            confirmExternal = true;
        }
        setBusy((prev) => ({ ...prev, [key]: 'test' }));
        try {
            await sendTest(key, to, confirmExternal);
            toast.success(t('emailTemplates.test.sent', { to }));
        } catch (err) {
            const data = err?.response?.data;
            toast.error(data?.error_key === 'invalid_recipient'
                ? t('emailTemplates.test.invalidRecipient')
                : t('emailTemplates.test.failed', { error: data?.error || '' }));
        } finally {
            setBusy((prev) => ({ ...prev, [key]: null }));
        }
    };

    const handleApplyShared = async (key) => {
        if (layoutValid[key] === false) {
            toast.error(t('emailTemplates.layout.invalidForm'));
            return;
        }
        const result = await Swal.fire({
            title: t('emailTemplates.layout.share.confirmTitle'),
            text: t('emailTemplates.layout.share.confirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('emailTemplates.layout.share.confirmButton'),
            cancelButtonText: t('common.cancel'),
        });
        if (!result.isConfirmed) return;
        setBusy((prev) => ({ ...prev, [key]: 'share' }));
        try {
            const res = await applySharedEmailLayout(key, drafts[key]);
            const shared = Object.fromEntries(res.data.applied.map((k) => [k, drafts[key][k]]));
            // every loaded tab takes the shared values; unsaved edits to other settings are kept
            const nextDrafts = {};
            Object.keys(layouts).forEach((k) => { nextDrafts[k] = { ...drafts[k], ...shared }; });
            setLayouts((prev) => Object.fromEntries(Object.entries(prev).map(([k, layout]) => [k, {
                ...layout, initial: nextDrafts[k], baseline: { ...layout.baseline, ...shared }, version: layout.version + 1,
            }])));
            setDrafts((prev) => ({ ...prev, ...nextDrafts }));
            setLayoutErrors({});
            toast.success(t('emailTemplates.layout.share.done'));
        } catch (err) {
            const errors = err?.response?.data?.errors;
            if (errors) setLayoutErrors((prev) => ({ ...prev, [key]: errors }));
            toast.error(errors ? t('emailTemplates.layout.invalidForm') : t('emailTemplates.layout.share.error'));
        } finally {
            setBusy((prev) => ({ ...prev, [key]: null }));
        }
    };

    const handleSave = async (key) => {
        const layout = layouts[key];
        if (layout && layoutValid[key] === false) {
            toast.error(t('emailTemplates.layout.invalidForm'));
            return;
        }
        setSavingKey(key);
        try {
            await updateEmailTemplate(key, templates[key] || {});
            if (layout && JSON.stringify(drafts[key]) !== JSON.stringify(layout.baseline)) {
                try {
                    const res = await updateEmailLayout(key, drafts[key]);
                    setLayouts((prev) => ({ ...prev, [key]: { ...prev[key], baseline: res.data.values } }));
                    setLayoutErrors((prev) => ({ ...prev, [key]: {} }));
                } catch (err) {
                    const errors = err?.response?.data?.errors;
                    if (errors) {
                        setLayoutErrors((prev) => ({ ...prev, [key]: errors }));
                        toast.error(t('emailTemplates.layout.invalidForm'));
                        return;
                    }
                    throw err;
                }
            }
            toast.success(t('emailTemplates.success.saved'));
        } catch {
            toast.error(t('emailTemplates.errors.save'));
        } finally {
            setSavingKey(null);
        }
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <h4 className="heading mb-1">{t('emailTemplates.title')}</h4>
                    <p className="text-muted mb-4">{t('emailTemplates.subtitle')}</p>

                    <EmailBrandingSection onSaved={setBranding} />

                    <Card>
                        <Card.Body>
                            <div className="alert alert-info py-2 mb-3">
                                {t('emailTemplates.placeholderHint')}{' '}
                                <code>{PLACEHOLDER_TOKENS.join(', ')}</code>
                            </div>
                            <Tabs activeKey={activeKey} onSelect={setActiveKey} className="mb-3">
                                {TEMPLATE_KEYS.map((key) => {
                                    const tpl = templates[key] || {};
                                    return (
                                        <Tab key={key} eventKey={key} title={t(`emailTemplates.keys.${key}`)}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('emailTemplates.subjectLabel')}</Form.Label>
                                                <Form.Control
                                                    value={tpl.subject || ''}
                                                    onChange={(e) => setField(key, 'subject', e.target.value)}
                                                    placeholder={t('emailTemplates.subjectPlaceholder')}
                                                />
                                            </Form.Group>

                                            <Form.Check
                                                type="switch"
                                                id={`${key}-branded-layout`}
                                                className="mb-2"
                                                label={t('emailTemplates.toggles.brandedLayout')}
                                                checked={!!tpl.use_branded_layout}
                                                onChange={(e) => setField(key, 'use_branded_layout', e.target.checked)}
                                            />
                                            <Alert variant={tpl.use_branded_layout ? 'success' : 'secondary'} className="py-2">
                                                {t(tpl.use_branded_layout ? 'emailTemplates.layout.activeNotice' : 'emailTemplates.layout.inactiveNotice')}
                                            </Alert>

                                            {layouts[key] && (
                                                <div className="email-test-bar mb-3">
                                                    <Form.Label className="fw-semibold mb-1">{t('emailTemplates.test.title')}</Form.Label>
                                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                                        <Form.Control
                                                            type="email"
                                                            className="email-test-recipient"
                                                            aria-label={t('emailTemplates.test.recipientLabel')}
                                                            value={testTo[key] ?? layouts[key].meta.test_recipient}
                                                            onChange={(e) => setTestTo((prev) => ({ ...prev, [key]: e.target.value }))}
                                                        />
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={() => handleSendTest(key)}
                                                            disabled={busy[key] === 'test'}
                                                        >
                                                            {busy[key] === 'test' ? t('emailTemplates.test.sending') : t('emailTemplates.test.button')}
                                                        </Button>
                                                    </div>
                                                    <Form.Text className="d-block">{t('emailTemplates.test.hint')}</Form.Text>
                                                </div>
                                            )}

                                            <Tabs
                                                id={`${key}-design-tabs`}
                                                variant="pills"
                                                activeKey={subTabs[key] || 'branded'}
                                                onSelect={(k) => setSubTabs((prev) => ({ ...prev, [key]: k }))}
                                                className="mb-3"
                                            >
                                                <Tab eventKey="branded" title={t('emailTemplates.layout.subtabBranded')}>
                                                    {layouts[key] ? (<>
                                                        <div className="mb-3">
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                onClick={() => handleApplyShared(key)}
                                                                disabled={busy[key] === 'share'}
                                                            >
                                                                {t('emailTemplates.layout.share.button')}
                                                            </Button>
                                                        </div>
                                                        <Row className="g-3">
                                                            <Col xl={7}>
                                                                <EmailLayoutSettings
                                                                    key={`${key}-${layouts[key].version}`}
                                                                    schema={layouts[key].schema}
                                                                    initialValues={layouts[key].initial}
                                                                    serverErrors={layoutErrors[key] || {}}
                                                                    onChange={(values) => setDrafts((prev) => ({ ...prev, [key]: values }))}
                                                                    onValidity={(ok) => setLayoutValid((prev) => ({ ...prev, [key]: ok }))}
                                                                    onReset={(sectionId) => handleLayoutReset(key, sectionId)}
                                                                />
                                                            </Col>
                                                            <Col xl={5}>
                                                                <Form.Label>{t('emailTemplates.preview.label')}</Form.Label>
                                                                <div className="email-preview-sticky">
                                                                    <BrandedEmailPreview templateKey={key} subject={tpl.subject} settings={drafts[key]} />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </>) : (
                                                        <div className="text-center py-4">
                                                            {layoutFailed[key]
                                                                ? <Button variant="outline-primary" size="sm" onClick={() => loadLayout(key)}>{t('emailTemplates.layout.loadError')}</Button>
                                                                : <div className="spinner-border text-primary" role="status" />}
                                                        </div>
                                                    )}
                                                </Tab>
                                                <Tab eventKey="old" title={t('emailTemplates.layout.subtabOld')}>
                                                    <Row className="g-3 mb-3">
                                                        <Col lg={7}>
                                                            <Form.Label>{t('emailTemplates.bodyLabel')}</Form.Label>
                                                            <RichTextEditor
                                                                value={tpl.body}
                                                                onChange={(html) => setField(key, 'body', html)}
                                                                placeholderTokens={PLACEHOLDER_TOKENS}
                                                            />
                                                        </Col>
                                                        <Col lg={5}>
                                                            <Form.Label>{t('emailTemplates.preview.label')}</Form.Label>
                                                            <EmailPreview
                                                                subject={tpl.subject}
                                                                body={tpl.body}
                                                                includeLogo={tpl.include_logo}
                                                                attachInvoicePdf={tpl.attach_invoice_pdf}
                                                                branding={branding}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <div className="d-flex flex-wrap gap-4 align-items-center mb-3">
                                                        <Form.Check
                                                            type="switch"
                                                            id={`${key}-include-logo`}
                                                            label={t('emailTemplates.toggles.includeLogo')}
                                                            checked={!!tpl.include_logo}
                                                            onChange={(e) => setField(key, 'include_logo', e.target.checked)}
                                                        />
                                                    </div>
                                                </Tab>
                                            </Tabs>

                                            <Form.Check
                                                type="switch"
                                                id={`${key}-attach-pdf`}
                                                className="mb-3"
                                                label={t('emailTemplates.toggles.attachInvoicePdf')}
                                                checked={!!tpl.attach_invoice_pdf}
                                                onChange={(e) => setField(key, 'attach_invoice_pdf', e.target.checked)}
                                            />
                                            <Form.Text className="d-block mb-2">{t('emailTemplates.layout.saveHint')}</Form.Text>

                                            <Button
                                                variant="primary"
                                                onClick={() => handleSave(key)}
                                                disabled={savingKey === key}
                                            >
                                                {savingKey === key ? t('common.saving') : t('common.save')}
                                            </Button>
                                        </Tab>
                                    );
                                })}
                            </Tabs>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}
